import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Bell, Cake, CheckCircle2, Clock, ExternalLink, Heart,
  Loader2, MessageCircle, Phone, RotateCcw, ShieldAlert, Sparkles, UserCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API } from '@/App';

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const SEV_STYLE = {
  urgent: { ring: 'ring-rose-300', bg: 'bg-rose-50', tag: 'bg-rose-600 text-white', label: 'URGENT' },
  warn:   { ring: 'ring-amber-300', bg: 'bg-amber-50', tag: 'bg-amber-500 text-white', label: 'SOON' },
  info:   { ring: 'ring-slate-200', bg: 'bg-slate-50', tag: 'bg-slate-500 text-white', label: 'INFO' },
};

const KIND_ICON = {
  driver_share:            Phone,
  hotel_checkin:           Sparkles,
  missing_driver:          AlertTriangle,
  missing_guide:           UserCheck,
  missing_others:          AlertTriangle,
  vendor_payment_due:      ShieldAlert,
  celebration_birthday:    Cake,
  celebration_anniversary: Heart,
};

/**
 * Open WhatsApp for each recipient sequentially. WhatsApp's wa.me deep
 * link can only target ONE chat at a time, so we open them one at a time
 * with a tiny gap. The recipient list comes from the backend
 * `/alerts/broadcast` and already includes:
 *   - team members flagged `alerts_recipient`
 *   - extra "always-CC" numbers configured in Alerts → Settings.
 * Nothing is hardcoded in the frontend anymore.
 */
export const buildBroadcastQueue = (recipients, message) => {
  const text = encodeURIComponent(message || '');
  return (recipients || [])
    .filter(r => (r.phone || '').trim())
    .map(r => {
      const phone = (r.phone || '').replace(/\D+/g, '');
      // wa.me strips leading + automatically. For 10-digit Indian numbers
      // without country code, prefix 91.
      const e164 = phone.length === 10 ? `91${phone}` : phone;
      return { ...r, url: `https://wa.me/${e164}?text=${text}` };
    });
};

export const useAlerts = (intervalMs = 60_000) => {
  const [data, setData] = useState({ alerts: [], counts: { total: 0 }, done: [] });
  const [loading, setLoading] = useState(true);
  const load = useCallback(async ({ includeDone = false } = {}) => {
    try {
      const url = `${API}/pollen/alerts${includeDone ? '?include_done=1' : ''}`;
      const res = await fetch(url, { credentials: 'include', headers: authHeaders() });
      if (!res.ok) return;
      const d = await res.json();
      setData(d);
    } catch (_) { /* noop */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(() => load(), intervalMs);
    return () => clearInterval(id);
  }, [load, intervalMs]);
  return { ...data, loading, reload: load };
};

const markDone = async (id) => {
  const res = await fetch(`${API}/pollen/alerts/${id}/done`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ note: '' }),
  });
  if (!res.ok) throw new Error('Failed to mark done');
};
const reopen = async (id) => {
  const res = await fetch(`${API}/pollen/alerts/${id}/reopen`, {
    method: 'POST', credentials: 'include', headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to reopen');
};

export const AlertRow = ({ alert, onDone, onReopen, compact = false }) => {
  const navigate = useNavigate();
  const sev = SEV_STYLE[alert.severity] || SEV_STYLE.info;
  const Icon = KIND_ICON[alert.kind] || AlertTriangle;
  const isDone = alert.done;
  // Build a tiny single-alert WhatsApp message for the "Send" button on
  // each row — useful when the operator only wants to nudge the team
  // about ONE thing rather than waiting for the daily broadcast.
  const sendSingle = async () => {
    try {
      const t = localStorage.getItem('session_token');
      // Pull recipients + CC numbers in one call so we share the same
      // configuration path with the daily broadcast (team members flagged
      // alerts_recipient + extra "always-CC" numbers from settings).
      const r = await fetch(`${API}/pollen/alerts/broadcast`, {
        credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      const data = r.ok ? await r.json() : { recipients: [] };
      const recipients = data.recipients || [];
      if (recipients.length === 0) {
        toast.error('No alerts recipients yet — mark team members in Team page or add CC numbers in Alerts → Settings.');
        return;
      }
      const base = window.location.origin;
      const link = alert.link ? `${base}${alert.link}` : '';
      const msg = [
        `🌼 *Pollen Dots*`,
        '',
        alert.title,
        alert.description || '',
        link ? `\n${link}` : '',
      ].filter(Boolean).join('\n');
      const queue = buildBroadcastQueue(recipients, msg);
      for (let i = 0; i < queue.length; i++) {
        if (i > 0) await new Promise(rs => setTimeout(rs, 600));
        window.open(queue[i].url, '_blank', 'noopener');
      }
      toast.success(`Opening WhatsApp for ${queue.length} recipient${queue.length === 1 ? '' : 's'}`);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div
      className={`rounded-lg border ring-1 ${sev.ring} ${isDone ? 'bg-emerald-50/60 opacity-75' : sev.bg} p-3 flex flex-col gap-1.5`}
      data-testid={`alert-row-${alert.id}`}
    >
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 mt-0.5 shrink-0 text-foreground/70" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {!isDone && (
              <Badge className={`text-[9px] px-1.5 py-0 h-4 ${sev.tag}`}>{sev.label}</Badge>
            )}
            {isDone && (
              <Badge className="text-[9px] px-1.5 py-0 h-4 bg-emerald-600 text-white">DONE</Badge>
            )}
            <span className={`text-sm font-medium truncate ${isDone ? 'line-through' : ''}`}>{alert.title}</span>
          </div>
          {!compact && alert.description && (
            <p className={`text-xs mt-0.5 ${isDone ? 'text-muted-foreground' : 'text-foreground/75'}`}>{alert.description}</p>
          )}
          {isDone && alert.done_by && (
            <p className="text-[10px] text-emerald-700 mt-0.5">
              <CheckCircle2 className="w-3 h-3 inline mr-0.5" />
              Cleared by {alert.done_by.split('@')[0]}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 justify-end">
        {alert.link && (
          <Button
            size="sm" variant="ghost"
            className="h-7 text-xs px-2"
            onClick={() => navigate(alert.link)}
            data-testid={`alert-open-${alert.id}`}
          >
            <ExternalLink className="w-3 h-3 mr-1" />
            {alert.action_label || 'Open'}
          </Button>
        )}
        {!isDone && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={sendSingle}
            title="Send this alert via WhatsApp to all designated team members"
            data-testid={`alert-send-${alert.id}`}
          >
            <MessageCircle className="w-3 h-3 mr-1" />Send
          </Button>
        )}
        {!isDone && (
          <Button
            size="sm"
            className="h-7 text-xs px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={async () => {
              try { await markDone(alert.id); onDone && onDone(alert.id); }
              catch (e) { toast.error(e.message); }
            }}
            data-testid={`alert-done-${alert.id}`}
          >
            <CheckCircle2 className="w-3 h-3 mr-1" />Done
          </Button>
        )}
        {isDone && (
          <Button
            size="sm" variant="ghost"
            className="h-7 text-xs px-2 text-muted-foreground"
            onClick={async () => {
              try { await reopen(alert.id); onReopen && onReopen(alert.id); }
              catch (e) { toast.error(e.message); }
            }}
            data-testid={`alert-reopen-${alert.id}`}
          >
            <RotateCcw className="w-3 h-3 mr-1" />Reopen
          </Button>
        )}
      </div>
    </div>
  );
};

// Compact dashboard ribbon — top 5 active alerts. Folds to a one-line bar
// when there are zero active items so it never dominates the dashboard.
export const AlertsRibbon = () => {
  const navigate = useNavigate();
  const { alerts, counts, loading, reload } = useAlerts();
  const top = alerts.slice(0, 5);
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Checking alerts…
      </div>
    );
  }
  if (counts.total === 0) {
    return (
      <div className="rounded-lg border bg-emerald-50/60 border-emerald-200 text-emerald-800 px-3 py-2 text-xs flex items-center gap-2" data-testid="alerts-clean">
        <CheckCircle2 className="w-3.5 h-3.5" />
        All clear — no operational alerts right now.
      </div>
    );
  }
  return (
    <Card className="overflow-hidden" data-testid="alerts-ribbon">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-sm flex items-center gap-1.5">
          <Bell className="w-4 h-4 text-amber-600" />
          Action Required ({counts.total})
          {counts.urgent > 0 && <Badge className="ml-1 h-4 px-1.5 text-[10px] bg-rose-600 text-white">{counts.urgent} urgent</Badge>}
        </CardTitle>
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate('/pollen-dots/alerts')} data-testid="alerts-see-all">
          See all →
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {top.map(a => (
          <AlertRow
            key={a.id}
            alert={a}
            compact
            onDone={() => reload()}
          />
        ))}
      </CardContent>
    </Card>
  );
};

export default AlertsRibbon;
